import { ReactNode } from 'react';

import { Box, Stack } from '@mui/material';
import clsx from 'clsx';

import { EllipsisWithTooltip } from '../../../components/tooltip';
import { tokens } from '../../../theme/generated/tokens';
import { SxProps } from '../../../types';
import { classNamesFor } from '../../../utils/classNames';
import { layoutVars } from '../../variables';

import { pageMarketingBannerPortalId } from './pageMarketingBannerPortalId';

const classNamesFactory = classNamesFor('PageHeader');

const classNames = {
  root: classNamesFactory.root(),
  breadcrumbs: classNamesFactory.element('breadcrumbs'),
  titleRow: classNamesFactory.element('titleRow'),
  titleArea: classNamesFactory.element('titleArea'),
  suffix: classNamesFactory.element('suffix'),
  endContent: classNamesFactory.element('endContent'),
  metadata: classNamesFactory.element('metadata'),
  marketingBannerPortal: classNamesFactory.element('marketingBannerPortal'),
};

export type PageHeaderProps = {
  /**
   * Page title. Plain string → h3 with truncation + tooltip on overflow; any ReactNode renders as-is.
   */
  heading: ReactNode;
  /**
   * Icon buttons tied to the entity, e.g. copy ID or open in new tab.
   */
  suffix?: ReactNode;
  /**
   * Right-aligned content, e.g. CTA buttons, a date-range picker, or a button pair.
   */
  endContent?: ReactNode;
  /**
   * Breadcrumb navigation rendered above the title row.
   * Use on pages nested 2+ levels deep.
   * @see `Breadcrumbs` and `Breadcrumb` in `libs/design-system/src/components/navigation/breadcrumbs`
   */
  breadcrumbs?: ReactNode;
  /**
   * Bottom padding. If the number or string, it will be used as a `paddingBottom` value.
   * If `true`, the `paddingBottom` value will be taken from `layoutVars.sectionSpacing`.
   * If `false`, no padding will be applied.
   * Pass `true` if containing `Page` component is not `stack`.
   */
  pb?: boolean | number | string;
  /**
   * Slot rendered below the title row.
   * Use for status badges, tags, or contextual attributes.
   */
  metadata?: ReactNode;
  sx?: SxProps;
  testId?: string;
  className?: string;
};

/**
 * Page-level heading block with a required title and optional slots for
 * breadcrumbs, actions, and metadata.
 *
 * Renders an in-flow portal outlet after the chrome for marketing banners
 * (`pageMarketingBannerPortalId` / app `PAGE_MARKETING_BANNER`).
 */
export const PageHeader = ({
  heading,
  suffix,
  endContent,
  breadcrumbs,
  metadata,
  testId = 'page-header',
  className,
  sx,
  pb,
}: PageHeaderProps) => {
  return (
    <Box
      width="100%"
      data-testid={testId}
      pb={
        pb === true
          ? layoutVars.refs.sectionSpacing
          : pb === false
          ? undefined
          : pb
      }
    >
      <Stack
        gap={8}
        width="100%"
        alignItems="flex-start"
        className={clsx(classNames.root, className)}
        sx={sx}
      >
        {!!breadcrumbs && (
          <Box className={classNames.breadcrumbs}>{breadcrumbs}</Box>
        )}
        <Stack
          direction="row"
          gap={12}
          alignItems="center"
          width="100%"
          className={classNames.titleRow}
        >
          <Stack
            direction="row"
            gap={4}
            alignItems="center"
            flexGrow={1}
            minWidth={0}
            width="max-content"
            className={classNames.titleArea}
          >
            {typeof heading === 'string' ? (
              <EllipsisWithTooltip
                TypographyProps={{
                  variant: 'h3',
                  color: tokens.theme.fg.structural.primary,
                }}
                minWidth={0}
              >
                {heading}
              </EllipsisWithTooltip>
            ) : (
              <Box typography="h3" color={tokens.theme.fg.structural.primary}>
                {heading}
              </Box>
            )}
            {!!suffix && (
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
                className={classNames.suffix}
              >
                {suffix}
              </Box>
            )}
          </Stack>
          {!!endContent && (
            <Stack
              direction="row"
              gap={12}
              alignItems="center"
              flexShrink={0}
              className={classNames.endContent}
            >
              {endContent}
            </Stack>
          )}
        </Stack>
        {!!metadata && (
          <Box width="100%" className={classNames.metadata}>
            {metadata}
          </Box>
        )}
      </Stack>
      <Box
        id={pageMarketingBannerPortalId}
        className={classNames.marketingBannerPortal}
        data-testid="page-header-marketing-banner-portal"
        width="100%"
        // In-flow inside Page/<main> only — never MainLayout sticky chrome
        // (keeps banners out of sidebar / app navbar stacking contexts).
        sx={{
          position: 'relative',
          minWidth: 0,
          '&:empty': { display: 'none' },
        }}
      />
    </Box>
  );
};

PageHeader.classNames = classNames;
